import {
  Fragment,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useApolloClient } from '@apollo/client/react';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import { GET_USERS } from '@atom/graph/user';
import { GET_USER_GROUPS } from '@atom/graph/userGroup';
import { useUserProfile } from '@atom/hooks/useUserProfile';
import { Autocomplete, Avatar, Chip, Icon, List, Progress } from '@atom/mui';
import { PeopleIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { TaskUserStatus } from '@atom/types/task';
import {
  GroupFilterOption,
  UserDetail,
  UserFilterOption,
} from '@atom/types/user';
import { UserGroup } from '@atom/types/userGroups';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { getColorFromColorId } from '@atom/utilities/colorUtilities';
import {
  getMeUserOption,
  getMyGroupsOption,
  getUserFullName,
  MY_GROUPS_ID,
} from '@atom/utilities/userUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

const { ListItemText } = List;

type DebouncedSearch = (name: string, ids?: string[]) => void;
type UserOrGroup =
  | UserDetail
  | UserGroup
  | UserFilterOption
  | GroupFilterOption;

const DEBOUNCE_TIME = 300;
const MIN_CHAR = 3;
const SEARCH_LIMIT = 250;
const SEARCH_PAGE = 1;
const UNASSIGNED_USER_ID = 'unassigned';
const UNASSIGNED_USER: UserDetail = {
  baseLocation: '',
  budgets: [],
  certifications: [],
  createdDate: 0,
  department: '',
  email: '',
  employeeId: '',
  firstName: 'Unassigned',
  id: UNASSIGNED_USER_ID,
  inheritedRoles: [],
  isActive: true,
  isVerified: true,
  lastName: '',
  level: 1,
  location: { type: 'Point', coordinates: [0, 0] },
  notifications: [],
  photoUrl: '',
  rate: 0,
  role: 'Admin',
  roles: [],
  status: TaskUserStatus.ASSIGNED,
  timeEntryApproverInfo: [],
  timezoneLocation: '',
  title: '',
  phoneNumber: '',
  updatedDate: 0,
  preferences: {},
};

const styles = {
  avatar: {
    marginRight: '0.5rem',
  },
  chip: {
    marginTop: '5px',
    marginRight: '5px',
    backgroundColor: colors.neutral.ash,
    borderRadius: '20px',
  },
  groupIcon: {
    fontSize: fonts.xxl,
  },
};

const isUser = (userOrGroup: UserOrGroup): userOrGroup is UserDetail => {
  return userOrGroup.hasOwnProperty('firstName');
};

const getUserAvatar = (user: UserDetail): ReactNode => (
  <Avatar style={styles.avatar} src={user.photoUrl} alt={user.firstName} />
);

const getUserGroupAvatar = (group: UserGroup): ReactNode => (
  <Avatar
    style={{
      ...styles.avatar,
      // Design bg color is unavailable in utility method, hardcoding here instead
      background:
        group.id === MY_GROUPS_ID
          ? colors.neutral.gray
          : getColorFromColorId(group.colorId || 0),
    }}
    alt={group.name}
  >
    <Icon
      style={styles.groupIcon}
      color={colors.neutral.white}
      icon={PeopleIcon}
    />
  </Avatar>
);

const getLabel = (userOrGroup: UserOrGroup): string =>
  isUser(userOrGroup)
    ? getUserFullName(userOrGroup)
    : (userOrGroup as GroupFilterOption).name;

const getDefaultUserSubtext = (user: UserDetail): string => user.email;

const getDefaultGroupSubtext = (group: UserGroup): string =>
  (group.groupPath || []).join(' / ');

type AssigneeFilterProps = {
  userIds: string[];
  groupIds: string[];
  onChange: (userIds: string[], groupIds: string[]) => void;
  disabled?: boolean;
  // Adds the Me / Unassigned / My Groups options to an empty search
  includeQuickOptions?: boolean;
  label?: string;
  placeholder?: string;
  getUserSubtext?: (user: UserDetail) => string;
  getGroupSubtext?: (group: UserGroup) => string;
  // Wraps each selected group's chip, e.g. to show its members on hover
  renderGroupChip?: (chip: ReactElement, group: UserGroup) => ReactNode;
  filterGroups?: (group: UserGroup) => boolean;
  dataCy?: string;
};

const AssigneeFilter = ({
  userIds,
  groupIds,
  onChange,
  disabled,
  includeQuickOptions = false,
  label = 'Assigned To',
  placeholder = 'Search for users or groups',
  getUserSubtext = getDefaultUserSubtext,
  getGroupSubtext = getDefaultGroupSubtext,
  renderGroupChip,
  filterGroups,
  dataCy,
}: AssigneeFilterProps) => {
  const client = useApolloClient();

  const userProfile = useUserProfile();
  const myGroupIds = userProfile?.userGroupIds || [];

  const [query, setQuery] = useState<string>('');
  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [loadingPreselected, setLoadingPreselected] = useState<boolean>(false);
  const [results, setResults] = useState<UserOrGroup[]>([]);
  const [selected, setSelected] = useState<UserOrGroup[]>([]);

  const getUsersAndGroups = async (
    name: string = '',
    selectedUserIds: string[] = [],
    selectedGroupIds: string[] = [],
  ) => {
    const hasIds = selectedUserIds?.length || selectedGroupIds?.length;

    if (hasIds) {
      setLoadingPreselected(true);
    } else {
      setLoadingSearch(true);
    }

    const hasMyGroups = selectedGroupIds.includes(MY_GROUPS_ID);

    // If user selects only my groups, we must skip the call
    //   and resolve userGroups containing only the MY_GROUPS option
    const onlyMyGroups = hasMyGroups && selectedGroupIds.length === 1;

    // Ensure we don't send "MY_GROUPS" to GET_USER_GROUPS
    const userGroupIds = hasMyGroups
      ? R.without([MY_GROUPS_ID], selectedGroupIds)
      : selectedGroupIds;

    const [users, userGroups] = await Promise.all([
      ...(name || selectedUserIds?.length
        ? [
            client
              .query({
                query: GET_USERS,
                variables: {
                  input: R.reject(isNilOrEmpty, {
                    limit: SEARCH_LIMIT,
                    page: SEARCH_PAGE,
                    name,
                    showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
                    ...(selectedUserIds.length && { ids: selectedUserIds }),
                  }),
                },
              })
              .then(({ data }) => data.users.users),
          ]
        : [Promise.resolve([])]),
      ...((name || selectedGroupIds?.length) && !onlyMyGroups
        ? [
            client
              .query({
                query: GET_USER_GROUPS,
                variables: {
                  input: R.reject(isNilOrEmpty, {
                    limit: SEARCH_LIMIT,
                    page: SEARCH_PAGE,
                    showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
                    name,
                    ...(userGroupIds.length && { ids: userGroupIds }),
                  }),
                },
              })
              .then(({ data }) => {
                // Reattach My Groups option if it was present
                return hasMyGroups
                  ? [...data.userGroups.userGroups, getMyGroupsOption()]
                  : data.userGroups.userGroups;
              }),
          ]
        : [Promise.resolve(onlyMyGroups ? [getMyGroupsOption()] : [])]),
    ]);

    const unassigned = R.includes(UNASSIGNED_USER_ID, selectedUserIds || [])
      ? [UNASSIGNED_USER]
      : [];

    const allOptions = [...unassigned, ...users, ...userGroups];

    if (hasIds) {
      setSelected(allOptions);
      setLoadingPreselected(false);
    } else {
      setResults(allOptions);
      setLoadingSearch(false);
    }
  };

  const getUsersAndGroupsDebounced = useCallback<DebouncedSearch>(
    debounce(getUsersAndGroups, DEBOUNCE_TIME),
    [],
  );

  useEffect(() => {
    if (query.length >= MIN_CHAR) {
      getUsersAndGroupsDebounced(query);
    } else {
      setResults(
        includeQuickOptions
          ? [getMeUserOption(userProfile), UNASSIGNED_USER, getMyGroupsOption()]
          : [],
      );
    }
  }, [query]);

  const allIds = [...(userIds || []), ...(groupIds || [])];
  // callers commonly pass a fresh array on every render - key the hydration off
  // the ids themselves so an id we cannot resolve can't loop the query
  const idsKey = allIds.join(',');

  useEffect(() => {
    if (isNilOrEmpty(allIds)) {
      if (selected.length) {
        setSelected([]);
      }

      return;
    }

    // Only re-resolve names when the incoming ids and the chips have drifted
    // apart - hydrating a saved filter must refresh them, but the user's own
    // selections already match and shouldn't disable the input on every click.
    const hasDrifted = !R.isEmpty(
      R.symmetricDifference(
        allIds,
        selected.map(({ id }) => id),
      ),
    );

    if (hasDrifted) {
      getUsersAndGroups(null, userIds || [], groupIds || []);
    }
  }, [idsKey]);

  const handleChange = (allValues: UserOrGroup[]) => {
    // If user selects my groups while any of their is already selected,
    // - remove those groups from selected values
    const hasMyGroups = allValues.some(({ id }) => id === MY_GROUPS_ID);
    const values = hasMyGroups
      ? allValues.filter(({ id }) => !myGroupIds.includes(id))
      : allValues;

    setSelected(values);

    const updatedIds = values.reduce(
      (acc, userOrGroup) => {
        return isUser(userOrGroup)
          ? {
              ...acc,
              userIds: [...acc.userIds, userOrGroup.id],
            }
          : {
              ...acc,
              groupIds: [...acc.groupIds, userOrGroup.id],
            };
      },
      { userIds: [], groupIds: [] },
    );

    onChange(updatedIds.userIds, updatedIds.groupIds);
  };

  const handleDeselect = (id: string) => {
    setSelected(state => state.filter(userOrGroup => userOrGroup.id !== id));

    onChange(
      (userIds || []).filter(userId => userId !== id),
      (groupIds || []).filter(groupId => groupId !== id),
    );
  };

  const getOptionDisabled = (userOrGroup: UserOrGroup) => {
    const isInMyGroupsSelected =
      selected.some(({ id }) => id === MY_GROUPS_ID) &&
      myGroupIds.includes(userOrGroup.id);

    return (
      isInMyGroupsSelected || selected.some(({ id }) => id === userOrGroup.id)
    );
  };

  const visibleResults = results.filter(
    userOrGroup =>
      isUser(userOrGroup) ||
      !filterGroups ||
      filterGroups(userOrGroup as UserGroup),
  );

  const renderChip = (userOrGroup: UserOrGroup) => {
    const avatar = isUser(userOrGroup)
      ? getUserAvatar(userOrGroup)
      : getUserGroupAvatar(userOrGroup as UserGroup);

    const chip = (
      <Chip
        key={userOrGroup.id}
        style={styles.chip}
        // @ts-ignore
        avatar={avatar}
        label={getLabel(userOrGroup)}
        onDelete={() => handleDeselect(userOrGroup.id)}
        data-cy={dataCy && `${dataCy}Chip-${getLabel(userOrGroup)}`}
      />
    );

    return renderGroupChip && !isUser(userOrGroup) ? (
      <Fragment key={userOrGroup.id}>
        {renderGroupChip(chip, userOrGroup as UserGroup)}
      </Fragment>
    ) : (
      chip
    );
  };

  return (
    <Autocomplete
      multiple
      label={label}
      options={visibleResults}
      loading={loadingSearch}
      disabled={loadingPreselected || disabled}
      inputValue={query}
      onInputChange={(event, val) => setQuery(val || '')}
      getOptionLabel={getLabel}
      placeholder={!selected.length ? placeholder : ''}
      value={selected}
      onChange={(event, values) => handleChange(values as UserOrGroup[])}
      getOptionDisabled={getOptionDisabled}
      // the options are already narrowed by the search query server side
      filterOptions={options => options}
      data-cy={dataCy}
      renderOption={(optionProps, userOrGroup) => (
        <li key={userOrGroup.id} {...optionProps}>
          {isUser(userOrGroup)
            ? getUserAvatar(userOrGroup)
            : getUserGroupAvatar(userOrGroup)}
          <ListItemText
            primary={getLabel(userOrGroup)}
            secondary={
              isUser(userOrGroup)
                ? getUserSubtext(userOrGroup)
                : getGroupSubtext(userOrGroup as UserGroup)
            }
          />
        </li>
      )}
      renderValue={usersOrGroups => usersOrGroups.map(renderChip)}
      groupBy={userOrGroup => (isUser(userOrGroup) ? 'Users' : 'Groups')}
      endAdornment={loadingPreselected && <Progress size={20} />}
    />
  );
};

export default AssigneeFilter;
